import {
  defineDoctype,
  normalizeLifecycleContract,
  type DoctypeCatalogMetadata,
  type DoctypeDefinition,
  type DocumentLifecycleContract,
} from "@kubdee/sdk";
import { crmActivities } from "./schemas/activities";
import { crmContacts } from "./schemas/contacts";
import { crmDeals } from "./schemas/deals";
import { crmLeadSources } from "./schemas/lead-sources";
import { crmLeads } from "./schemas/leads";
import { crmLostReasons } from "./schemas/lost-reasons";
import { crmOrganizations } from "./schemas/organizations";

// ============================================================================
// CRM module — registered doctypes.
//
// Doctype Admin Epic (#347).
//
// This file is imported by `modules/crm/index.ts` and re-exported as
// `registeredDoctypes` on the module manifest. The platform boot loop in
// `apps/tenant-worker/src/middleware/auto-migrate.ts` reads the field
// across every enabled module and upserts the platform-default rows into
// `core_doctype_registry`. The boot glue is strictly generic — the CRM
// manifest does not need to know how its entries are consumed, and adding
// CRM doctypes in follow-up issues only requires appending to
// `crmDoctypes` below.
//
// History:
//   - C1 (#359) originally registered `customer` here as the master-data
//     pilot. #413 relocated that registration to `modules/sales/doctypes.ts`
//     because Customer is a Selling-module concept (ERPNext parity — Lead /
//     Opportunity stay in CRM; Customer moves once converted).
//   - #423 migrates the legacy `seed.ts` per-company numbering rows for
//     CRM's own transactional doctypes into `defaultSeries`, mirroring
//     the pattern established by sales (#422) / purchase (#413). The stale
//     `(MODULES.CRM, "quotation")` row is intentionally dropped because
//     quotation belongs to sales. Lead is registered now that migration
//     `004_add_lead_numbering` and the create route both support `lead_no`.
// ============================================================================

const MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

const GENERIC_MASTER_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    ...MASTER_CATALOG.policy,
    allowGenericCreate: true,
    allowGenericUpdate: true,
    allowGenericDelete: true,
  },
};

const DOCUMENT_CATALOG: DoctypeCatalogMetadata = {
  policy: {
    isSearchable: true,
    isExportable: true,
    isReportable: true,
  },
};

export const dealLifecycle: DocumentLifecycleContract =
  normalizeLifecycleContract({
    statusField: "current_state_key",
    docStatusField: "doc_status",
    draftStates: ["new_contact"],
    submittedStates: ["qualified", "proposal", "negotiation", "won"],
    cancelledStates: ["lost"],
    immutableStates: ["won", "lost"],
    editAllowedStates: ["new_contact", "qualified", "proposal", "negotiation"],
    actions: {
      submit: {
        label: "คัดกรอง",
        from: ["new_contact"],
        to: "qualified",
        docStatus: 1,
        reversalPolicy: "none",
        activityAction: "submitted",
      },
      approve: {
        label: "ปิดการขาย",
        from: ["negotiation"],
        to: "won",
        docStatus: 1,
        reversalPolicy: "none",
        activityAction: "won",
      },
      reject: {
        label: "ไม่สำเร็จ",
        from: ["new_contact", "qualified", "proposal", "negotiation"],
        to: "lost",
        docStatus: 2,
        reversalPolicy: "status_only",
        requiresReason: true,
        activityAction: "lost",
      },
    },
  });

export const leadLifecycle: DocumentLifecycleContract =
  normalizeLifecycleContract({
    statusField: "current_state_key",
    docStatusField: "doc_status",
    draftStates: ["new"],
    submittedStates: ["contacted", "qualified", "converted"],
    cancelledStates: ["unqualified"],
    immutableStates: ["converted", "unqualified"],
    editAllowedStates: ["new", "contacted", "qualified"],
    actions: {
      submit: {
        label: "ติดต่อ",
        from: ["new"],
        to: "contacted",
        docStatus: 1,
        reversalPolicy: "none",
        activityAction: "submitted",
      },
      approve: {
        label: "แปลงเป็นดีล",
        from: ["qualified"],
        to: "converted",
        docStatus: 1,
        reversalPolicy: "none",
        activityAction: "converted",
      },
      reject: {
        label: "ไม่ผ่าน",
        from: ["new", "contacted", "qualified"],
        to: "unqualified",
        docStatus: 2,
        reversalPolicy: "status_only",
        requiresReason: true,
        activityAction: "unqualified",
      },
    },
  });

/**
 * Deal (`ดีล`) — opportunity / pipeline doctype. Stays in CRM under the
 * ERPNext model (Lead / Opportunity own the pre-conversion lifecycle,
 * Customer moves to Selling once converted).
 *
 * Numbering is generated through `(MODULES.CRM, "deal")` from
 * `modules/crm/routes/deals.ts:328` (deal create) and
 * `modules/crm/hooks/lead-hooks.ts:176` (lead → deal conversion). The
 * resolved value is written into `crm_deals.deal_no`. The `defaultSeries`
 * prefix mirrors the legacy `seed.ts` value (`DL-{YYYY}-#####`) so
 * existing per-company counters keep their running value (per-company
 * row wins over the platform-default NULL fallback).
 */
export const dealDoctype = defineDoctype({
  id: "deal",
  moduleId: "crm",
  table: crmDeals,
  label: "ดีล",
  icon: "Handshake",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "deal_no",
  displayField: "name",
  imageField: null,
  searchFields: ["deal_no", "name", "status"],
  // Thai UI copy for user-facing columns on `crm_deals`. Conversion FKs
  // (`lead_id`, `customer_id`, `invoice_id`, `quotation_id`) are
  // intentionally omitted — those are wiring metadata, not user-facing.
  columnLabels: {
    deal_no: "เลขที่ดีล",
    name: "ชื่อดีล",
    contact_id: "ผู้ติดต่อ",
    organization_id: "องค์กร",
    status: "สถานะ",
    lost_reason_id: "เหตุผลปิดดีล",
    amount: "มูลค่า",
    currency: "สกุลเงิน",
    probability: "โอกาสสำเร็จ (%)",
    expected_close_date: "วันคาดว่าจะปิด",
    closed_at: "วันที่ปิด",
    assigned_to: "ผู้รับผิดชอบ",
    source: "แหล่งที่มา",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "DL-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  workflowTemplate: {
    name: "Sales Workflow",
    description: "ติดต่อใหม่ → คัดกรอง → เสนอราคา → เจรจา → ปิดการขาย/ไม่สำเร็จ",
    isDefault: true,
    states: [
      { key: "new_contact", label: "ติดต่อใหม่", stateType: "start", color: "#6366F1", sortOrder: 0, positionX: 250, positionY: 0 },
      { key: "qualified", label: "คัดกรองแล้ว", stateType: "intermediate", color: "#8B5CF6", sortOrder: 1, positionX: 250, positionY: 100 },
      { key: "proposal", label: "เสนอราคา", stateType: "intermediate", color: "#A855F7", sortOrder: 2, positionX: 250, positionY: 200 },
      { key: "negotiation", label: "เจรจาต่อรอง", stateType: "intermediate", color: "#F59E0B", sortOrder: 3, positionX: 250, positionY: 300 },
      { key: "won", label: "ปิดการขาย", stateType: "success", color: "#22C55E", sortOrder: 4, positionX: 100, positionY: 420 },
      { key: "lost", label: "ไม่สำเร็จ", stateType: "fail", color: "#EF4444", sortOrder: 5, positionX: 400, positionY: 420 },
    ],
    transitions: [
      { fromStateKey: "new_contact", toStateKey: "qualified", label: "คัดกรอง" },
      { fromStateKey: "new_contact", toStateKey: "lost", label: "ไม่ผ่าน", formType: "custom", customFormKey: "crm.lost-deal", actionHook: "crm.action.dealLost" },
      { fromStateKey: "qualified", toStateKey: "proposal", label: "เสนอราคา" },
      { fromStateKey: "qualified", toStateKey: "lost", label: "ไม่สำเร็จ", formType: "custom", customFormKey: "crm.lost-deal", actionHook: "crm.action.dealLost" },
      { fromStateKey: "proposal", toStateKey: "negotiation", label: "เจรจา" },
      { fromStateKey: "proposal", toStateKey: "lost", label: "ไม่สำเร็จ", formType: "custom", customFormKey: "crm.lost-deal", actionHook: "crm.action.dealLost" },
      { fromStateKey: "negotiation", toStateKey: "won", label: "ปิดการขาย", formType: "custom", customFormKey: "crm.won-deal", actionHook: "crm.action.dealWon" },
      { fromStateKey: "negotiation", toStateKey: "lost", label: "ไม่สำเร็จ", formType: "custom", customFormKey: "crm.lost-deal", actionHook: "crm.action.dealLost" },
    ],
  },
  lifecycle: dealLifecycle,
});

/**
 * Lead (`ลีด`) — first-touch CRM record before conversion to a deal.
 *
 * Registered in #427 follow-up (Path C) once the matching prerequisites
 * landed together: migration `004_add_lead_numbering` adds the `lead_no`
 * column, and `routes/leads.ts` now calls
 * `generateNextNumber({ module: "crm", doctype: "lead" })` on insert.
 * Workflow template moved here from `modules/crm/constants.ts` —
 * same draft → contacted → qualified → converted/unqualified shape.
 */
export const leadDoctype = defineDoctype({
  id: "lead",
  moduleId: "crm",
  table: crmLeads,
  label: "ลีด",
  icon: "UserPlus",
  features: ["naming", "custom_fields", "workflow", "print", "permissions", "automation", "list_view"],
  tier: 2,
  kind: "submittable",
  catalog: DOCUMENT_CATALOG,
  numberingField: "lead_no",
  displayField: "full_name",
  imageField: null,
  searchFields: ["lead_no", "full_name", "email", "phone", "mobile"],
  columnLabels: {
    lead_no: "เลขที่ลีด",
    first_name: "ชื่อ",
    last_name: "นามสกุล",
    full_name: "ชื่อเต็ม",
    email: "อีเมล",
    phone: "โทรศัพท์",
    mobile: "มือถือ",
    line_id: "LINE ID",
    organization_name: "องค์กร",
    job_title: "ตำแหน่ง",
    website: "เว็บไซต์",
    status: "สถานะ",
    source: "แหล่งที่มา",
    score: "คะแนน",
    assigned_to: "ผู้รับผิดชอบ",
    remarks: "หมายเหตุ",
  },
  defaultSeries: {
    prefix: "LD-{YYYY}-#####",
    resetEvery: "yearly",
    seriesName: "Standard",
  },
  workflowTemplate: {
    name: "Lead Workflow",
    description: "ใหม่ → ติดต่อแล้ว → คัดกรอง → แปลงเป็นดีล/ไม่ผ่าน",
    isDefault: true,
    states: [
      { key: "new", label: "ใหม่", stateType: "start", color: "#3B82F6", sortOrder: 0, positionX: 250, positionY: 0 },
      { key: "contacted", label: "ติดต่อแล้ว", stateType: "intermediate", color: "#8B5CF6", sortOrder: 1, positionX: 250, positionY: 100 },
      { key: "qualified", label: "คัดกรองแล้ว", stateType: "intermediate", color: "#F59E0B", sortOrder: 2, positionX: 250, positionY: 200 },
      { key: "converted", label: "แปลงเป็นดีล", stateType: "success", color: "#22C55E", sortOrder: 3, positionX: 100, positionY: 320 },
      { key: "unqualified", label: "ไม่ผ่านคัดกรอง", stateType: "fail", color: "#EF4444", sortOrder: 4, positionX: 400, positionY: 320 },
    ],
    transitions: [
      { fromStateKey: "new", toStateKey: "contacted", label: "ติดต่อ" },
      { fromStateKey: "new", toStateKey: "unqualified", label: "ไม่ผ่าน" },
      { fromStateKey: "contacted", toStateKey: "qualified", label: "คัดกรอง" },
      { fromStateKey: "contacted", toStateKey: "unqualified", label: "ไม่ผ่าน" },
      { fromStateKey: "qualified", toStateKey: "converted", label: "แปลงเป็นดีล", formType: "custom", customFormKey: "crm.convert-lead", actionHook: "crm.action.convertLead" },
      { fromStateKey: "qualified", toStateKey: "unqualified", label: "ไม่ผ่าน" },
    ],
  },
  lifecycle: leadLifecycle,
});

// NOTE on `quotation`:
//   The legacy `seed.ts` row `(MODULES.CRM, "quotation", ...)` was a
//   stale duplicate of `(MODULES.SALES, "quotation")` (#356), with zero
//   `MODULES.CRM`/`"quotation"` call sites repo-wide. It is dropped, not
//   registered.

export const contactDoctype = defineDoctype({
  id: "contact",
  moduleId: "crm",
  table: crmContacts,
  label: "ผู้ติดต่อ",
  icon: "Contact",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "full_name",
  imageField: null,
  searchFields: ["full_name", "email", "phone", "mobile"],
});

export const organizationDoctype = defineDoctype({
  id: "organization",
  moduleId: "crm",
  table: crmOrganizations,
  label: "องค์กร",
  icon: "Building2",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name", "website", "phone", "email"],
});

export const activityDoctype = defineDoctype({
  id: "activity",
  moduleId: "crm",
  table: crmActivities,
  label: "กิจกรรม",
  icon: "Activity",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: DOCUMENT_CATALOG,
  numberingField: null,
  displayField: "title",
  imageField: null,
  searchFields: ["title", "description", "reference_type", "reference_id"],
});

export const leadSourceDoctype = defineDoctype({
  id: "lead_source",
  moduleId: "crm",
  table: crmLeadSources,
  label: "แหล่งที่มาของลีด",
  icon: "MapPinned",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: GENERIC_MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name"],
});

export const lostReasonDoctype = defineDoctype({
  id: "lost_reason",
  moduleId: "crm",
  table: crmLostReasons,
  label: "เหตุผลที่แพ้ดีล",
  icon: "CircleX",
  features: ["custom_fields", "permissions", "list_view"],
  tier: 2,
  kind: "normal",
  catalog: GENERIC_MASTER_CATALOG,
  numberingField: null,
  displayField: "name",
  imageField: null,
  searchFields: ["name"],
});

/**
 * Every doctype the CRM module wants registered with the Doctype Admin
 * platform. The order here is preserved in the Admin UI through
 * `listDoctypes()` sort.
 *
 * Note: `customer` is registered by `modules/sales/doctypes.ts` (#413),
 * not here. See the file header for the rationale.
 */
export const crmDoctypes: readonly DoctypeDefinition[] = Object.freeze([
  organizationDoctype,
  contactDoctype,
  leadSourceDoctype,
  lostReasonDoctype,
  dealDoctype,
  leadDoctype,
  activityDoctype,
]);
